// pages/invoicing/InvoicingPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import { useInvoiceStore } from '@/store/invoiceStore';
import { useClientStore, useUserStore } from '@/store';
import { Button } from '@/components/common/Button';
import { BaseTable, BaseColumn } from '@/components/common/table/BaseTable';
import { BaseTableCell } from '@/components/common/table/BaseTableCell';
import { useTableColumns } from '@/hooks/useTableColumns';
import { InvoiceModal } from './InvoiceModal';
import { MissingBusinessDetailsModal } from '@/components/modals/MissingBusinessDetailsModal';
import toast from 'react-hot-toast';
import { formatCurrency, formatDate } from '@/utils/helpers';
import { FREQUENCY_OPTIONS, TASK_PRIORITIES, TASK_STATUS } from '@/utils/constants';
import { setConfigPageFunctions, setQuotationsPageFunctions } from '@/components/keyboard';
import { useNavigate } from 'react-router-dom';

export const InvoicingPage: React.FC = () => {
  const { 
    pendingTasks, 
    invoices, 
    loading, 
    fetchPendingTasks, 
    fetchInvoices, 
    generateInvoiceFromTasks,
    selectedInvoice,
    setSelectedInvoice
  } = useInvoiceStore();
  const navigate = useNavigate();
  
  const { clients, loading: clientsLoading } = useClientStore();
  const { users } = useUserStore();
  const [activeTab, setActiveTab] = useState('pending');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<string[]>([]);
  const [missingDetailsData, setMissingDetailsData] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingCell, setEditingCell] = useState<{ taskId: string; columnKey: string } | null>(null);
  const [localAmounts, setLocalAmounts] = useState<Record<string, number>>({});
  
  // Use centralized columns hook
  const { columns: baseColumns, handleColumnChange } = useTableColumns('invoicing');

  useEffect(() => {
    if (activeTab === 'pending') {
      fetchPendingTasks();
    } else {
      fetchInvoices({ status: activeTab === 'generated' ? 'Generated' : 'Sent' });
    }
  }, [activeTab, fetchPendingTasks, fetchInvoices]);

  const tabs = [
    { id: 'pending', label: 'Pending Quotation' },
    { id: 'generated', label: 'Quotation' },
    { id: 'sent', label: 'Invoices' }
  ];

  useEffect(() => {
    setQuotationsPageFunctions({
      setActiveTab: (tabId: string) => {
        console.log('⌨️ Switching to tab:', tabId);
        setActiveTab(tabId)
      },
    });

    return () => {
      setConfigPageFunctions({
        setActiveTab:null
      });
    };
  }, [navigate]);

  const handleGenerateInvoice = async (taskIds: string[]) => {
    if (taskIds.length === 0) {
      toast.error('Please select at least one task');
      return;
    }

    const firstTask = pendingTasks.find(t => t._id === taskIds[0]);
    if (!firstTask) return;

    setSelectedTaskIds(taskIds);

    try {
      const response = await generateInvoiceFromTasks({
        taskIds,
        clientName: firstTask.client,
        dueDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString()
      });

      if (response.requiresBusinessDetails) {
        setMissingDetailsData({
          clientId: response.clientId,
          clientName: firstTask.client,
          missingFields: response.missingFields
        });
        setShowBusinessModal(true);
      } else if (response.invoice) {
        setSelectedInvoice(response.invoice);
        setShowInvoiceModal(true);
        fetchPendingTasks();
      }
    } catch (error: any) {
      // Error handling done in store
    }
  };

  const handleBusinessDetailsComplete = async () => {
    setShowBusinessModal(false);
    await handleGenerateInvoice(selectedTaskIds);
  };

  const toggleTask = (id: string) => {
    setSelectedTaskIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleInvoice = (id: string) => {
    setSelectedInvoiceIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleAll = (checked: boolean) => {
    if (activeTab === 'pending') {
      setSelectedTaskIds(checked ? pendingTasks.map(task => task._id!) : []);
    } else if (activeTab === 'generated') {
      const generatedInvoices = invoices.filter(inv => inv.status === 'Generated');
      setSelectedInvoiceIds(checked ? generatedInvoices.map(inv => inv._id!) : []);
    }
  };

  // Handle amount changes locally (no API integration for now)
  const handleAmountChange = (taskId: string, newAmount: number) => {
    setLocalAmounts(prev => ({
      ...prev,
      [taskId]: newAmount
    }));
  };

  // Handle cell edit start (only for amount field)
  const handleEditStart = (taskId: string, columnKey: string) => {
    if (columnKey === "amount") {
      setEditingCell({ taskId, columnKey });
    }
  };

  // Handle cell edit end
  const handleEditEnd = () => {
    setEditingCell(null);
  };

  const openModal = (type: string, task: any) => {
    console.log('Open modal:', type, task);
  };

  // Process columns with BaseTableCell renderer for Pending Quotation
  const pendingTaskColumns: BaseColumn[] = useMemo(() => {
    console.log(baseColumns,"vb")
    console.log('Pending tasks sample:', pendingTasks[0]);

    return baseColumns.map((col) => ({
      ...col,
      editable: col.key === "amount", // Only make amount field editable
      render: (task: any, index: number) => {
        if (col.key === "amount") {
          const amountValue = localAmounts[task._id] || task.quotationAmount || task.amount || 0;
          console.log('Amount column - Task ID:', task._id, 'Amount:', amountValue);
          
          const isEditing = editingCell?.taskId === task._id && editingCell?.columnKey === col.key;

          return (
            <BaseTableCell
              task={{ ...task, amount: amountValue }}
              column={col}
              index={index}
              isNewTask={false}
              hasRealId={!!task._id}
              isEditing={isEditing}
              onEditStart={() => handleEditStart(task._id, col.key)}
              onEditEnd={handleEditEnd}
              onExistingTaskChange={(taskId, key, value) => {
                if (key === 'amount') {
                  handleAmountChange(taskId, parseFloat(value) || 0);
                }
              }}
              clientsList={clients}
              users={users}
              openModal={openModal}
              serialStart={0}
            />
          );
        }

        if (col.key === "actions") {
          return (
            <div className="flex justify-center space-x-2">
              <button
                className='bg-blue-500 px-2 rounded-md text-white text-sm py-1 hover:bg-blue-600 transition-colors'
                onClick={() => {
                  // Handle view action
                  console.log('View task:', task);
                }}
              >
                View
              </button>
              <button
                className='bg-green-500 px-2 rounded-md text-white text-sm py-1 hover:bg-green-600 transition-colors'
                onClick={(e) => {
                  e.stopPropagation();
                  handleGenerateInvoice([task._id!]);
                }}
              >
                Generate
              </button>
            </div>
          );
        }

        // For all other columns, use BaseTableCell as before
        return (
          <BaseTableCell
            task={task}
            column={col}
            index={index}
            isNewTask={false}
            isEditing={false}
            onEditStart={() => {}}
            onEditEnd={() => {}}
            onExistingTaskChange={() => {}}
            clientsList={clients}
            users={users}
            openModal={openModal}
            totalTasks={pendingTasks.length}
            serialStart={0}
          />
        );
      },
    }));
  }, [baseColumns, pendingTasks.length, clients, users, editingCell, localAmounts]);

  // Invoice columns for Quotation tab (with editable amount)
  const quotationColumns: BaseColumn[] = useMemo(() => [
    { 
      key: "serial", 
      label: "#", 
      sortable: false, 
      visible: true, 
      width: "50px",
      render: (invoice, index) => invoices.length - index
    },
    { 
      key: "invoiceNumber", 
      label: "INVOICE #", 
      sortable: true, 
      visible: true,
      render: (invoice) => invoice.invoiceNumber
    },
    { 
      key: "client", 
      label: "CLIENT", 
      sortable: true, 
      visible: true,
      render: (invoice) => invoice.client?.name || invoice.clientName
    },
    { 
      key: "date", 
      label: "DATE", 
      sortable: true, 
      visible: true,
      render: (invoice) => formatDate(invoice.createdAt)
    },
    { 
      key: "dueDate", 
      label: "DUE DATE", 
      sortable: true, 
      visible: true,
      render: (invoice) => formatDate(invoice.dueDate)
    },
    { 
      key: "totalAmount", 
      label: "AMOUNT", 
      sortable: true, 
      visible: true,
      render: (invoice) => {
        const amountValue = localAmounts[invoice._id] || invoice.totalAmount || 0;
        const isEditing = editingCell?.taskId === invoice._id && editingCell?.columnKey === "totalAmount";

        if (isEditing) {
          return (
            <input
              type="number"
              value={amountValue}
              onChange={(e) => handleAmountChange(invoice._id, parseFloat(e.target.value) || 0)}
              onBlur={handleEditEnd}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleEditEnd();
                if (e.key === 'Escape') handleEditEnd();
              }}
              className="w-full h-6 border border-blue-500 rounded px-2 text-sm outline-none"
              autoFocus
            />
          );
        }

        return (
          <div 
            className="w-full h-full flex items-center px-2 text-sm cursor-pointer"
            style={{ height: "24px" }}
            onClick={() => handleEditStart(invoice._id, "totalAmount")}
          >
            <span className="truncate w-full">
              {formatCurrency(amountValue)}
            </span>
          </div>
        );
      }
    },
    { 
      key: "status", 
      label: "STATUS", 
      sortable: true, 
      visible: true,
      render: (invoice) => (
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
          invoice.status === 'Sent' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
        }`}>
          {invoice.status}
        </span>
      )
    },
    { 
      key: "actions", 
      label: "ACTIONS", 
      visible: true, 
      width: "150px",
      render: (invoice) => (
        <div className="flex justify-center space-x-2">
          <button
            className='bg-blue-500 px-2 rounded-md text-white text-sm py-1 hover:bg-blue-600 transition-colors'
            onClick={() => {
              setSelectedInvoice(invoice);
              setShowInvoiceModal(true);
            }}
          >
            View
          </button>
          <button
            className='bg-green-500 px-2 rounded-md text-white text-sm py-1 hover:bg-green-600 transition-colors'
            onClick={() => {
              // Handle email action
              console.log('Email invoice:', invoice);
            }}
          >
            Email
          </button>
        </div>
      )
    },
  ], [invoices.length, setSelectedInvoice, editingCell, localAmounts]);

  // Invoice columns for Sent Invoices tab (read-only)
  const invoiceColumns: BaseColumn[] = useMemo(() => [
    { 
      key: "serial", 
      label: "#", 
      sortable: false, 
      visible: true, 
      width: "50px",
      render: (invoice, index) => invoices.length - index
    },
    { 
      key: "invoiceNumber", 
      label: "INVOICE #", 
      sortable: true, 
      visible: true,
      render: (invoice) => invoice.invoiceNumber
    },
    { 
      key: "client", 
      label: "CLIENT", 
      sortable: true, 
      visible: true,
      render: (invoice) => invoice.client?.name || invoice.clientName
    },
    { 
      key: "date", 
      label: "DATE", 
      sortable: true, 
      visible: true,
      render: (invoice) => formatDate(invoice.createdAt)
    },
    { 
      key: "dueDate", 
      label: "DUE DATE", 
      sortable: true, 
      visible: true,
      render: (invoice) => formatDate(invoice.dueDate)
    },
    { 
      key: "totalAmount", 
      label: "AMOUNT", 
      sortable: true, 
      visible: true,
      render: (invoice) => formatCurrency(invoice.totalAmount)
    },
    { 
      key: "status", 
      label: "STATUS", 
      sortable: true, 
      visible: true,
      render: (invoice) => (
        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
          invoice.status === 'Sent' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
        }`}>
          {invoice.status}
        </span>
      )
    },
    { 
      key: "actions", 
      label: "ACTIONS", 
      visible: true, 
      width: "150px",
      render: (invoice) => (
        <Button
          size="sm"
          variant="secondary"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedInvoice(invoice);
            setShowInvoiceModal(true);
          }}
        >
          View
        </Button>
      )
    },
  ], [invoices.length, setSelectedInvoice]);

  // Dropdown configuration for filters
  const dropdownConfig = useMemo(() => ({
    client: {
      items: clients.map((client) => ({
        id: client._id || client.id,
        label: client.name,
      })),
      showSearch: true,
      showAdd: true,
      addLabel: "Add Client",
      onAdd: () => {/* Handle add client */},
      loading: clientsLoading,
    },
    allottedTo: {
      items: users.map((user) => ({
        id: user._id || user.id,
        label: user.name,
      })),
      showSearch: true,
    },
    teamLeader: {
      items: users.map((user) => ({
        id: user._id || user.id,
        label: user.name,
      })),
      showSearch: true,
    },
    priority: {
      items: [
        { id: TASK_PRIORITIES.HIGH, label: "High" },
        { id: TASK_PRIORITIES.MEDIUM, label: "Medium" },
        { id: TASK_PRIORITIES.LOW, label: "Low" },
      ],
    },
    status: {
      items: [
        { id: TASK_STATUS.TODO, label: "To Do" },
        { id: TASK_STATUS.IN_PROCESS, label: "In Process" },
        { id: TASK_STATUS.IN_REVIEW, label: "In Review" },
        { id: TASK_STATUS.COMPLETED, label: "Completed" },
        { id: TASK_STATUS.ARCHIVED, label: "Archived" },
        { id: TASK_STATUS.HELP_REQUIRED, label: "Help Required" },
      ],
    },
    frequency: {
      items: [
        { id: FREQUENCY_OPTIONS.NONE, label: "None" },
        { id: FREQUENCY_OPTIONS.MONTHLY, label: "Monthly" },
        { id: FREQUENCY_OPTIONS.QUARTERLY, label: "Quarterly" },
        { id: FREQUENCY_OPTIONS.YEARLY, label: "Yearly" },
      ],
    },
  }), [clients, clientsLoading, users]);

  const selectedFilters = useMemo(() => ({
    client: [],
    allottedTo: [],
    teamLeader: [],
    priority: [],
    status: [],
    frequency: [],
  }), []);

  const handleFilterChange = (columnKey: string, ids: string[]) => {
    console.log(`Filter ${columnKey}:`, ids);
  };

  const currentData = activeTab === 'pending' ? pendingTasks : 
    invoices.filter(inv => inv.status === (activeTab === 'generated' ? 'Generated' : 'Sent'));

  const currentColumns = activeTab === 'pending' ? pendingTaskColumns : 
                        activeTab === 'generated' ? quotationColumns : invoiceColumns;

  const selectedItems = activeTab === 'pending' ? selectedTaskIds : 
                       activeTab === 'generated' ? selectedInvoiceIds : [];

  const handleToggleSelection = activeTab === 'pending' ? toggleTask : 
                               activeTab === 'generated' ? toggleInvoice : () => {};

  // Bulk actions for Quotation tab
  const handleArchiveInvoices = () => {
    if (selectedInvoiceIds.length === 0) return;
    console.log('Archive invoices:', selectedInvoiceIds);
    toast.success(`${selectedInvoiceIds.length} invoices archived`);
    setSelectedInvoiceIds([]);
  };

  const handleGenerateSelectedInvoices = () => {
    if (selectedInvoiceIds.length === 0) return;
    console.log('Generate invoices:', selectedInvoiceIds);
    toast.success(`Generating ${selectedInvoiceIds.length} invoices`);
  };

  return (
    <div className="h-full bg-[white] flex flex-col">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200 flex-shrink-0">
        {/* Tabs */}
        <div className="px-6 py-4 flex items-center space-x-8 border-b border-gray-200">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                pb-3 px-1 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tab.id
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex h-[calc(100vh-280px)] sm:h-[calc(100vh-240px)]`}>
        <div className="flex-1 overflow-hidden">
          <BaseTable
            data={currentData}
            columns={currentColumns}
            loading={loading}
            selectable={activeTab === 'pending' || activeTab === 'generated'}
            selectedItems={selectedItems}
            onToggleSelection={handleToggleSelection}
            onToggleAllSelection={toggleAll}
            getItemId={(item) => item._id || item.id}
            showFilters={activeTab === 'pending'}
            dropdownConfig={dropdownConfig}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            sortable={true}
            emptyMessage={
              activeTab === 'pending' 
                ? 'No completed tasks pending for invoice'
                : `No ${activeTab} invoices found`
            }
            selectionFooter={
              activeTab === 'pending' && selectedTaskIds.length > 0
                ? (count) => (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">
                        {count} task(s) selected
                      </span>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleGenerateInvoice(selectedTaskIds)}
                      >
                        Generate Invoice for Selected
                      </Button>
                    </div>
                  )
                : activeTab === 'generated' && selectedInvoiceIds.length > 0
                ? (count) => (
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">
                        {count} invoice(s) selected
                      </span>
                      <div className="flex space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={handleArchiveInvoices}
                        >
                          Archive
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleGenerateSelectedInvoices}
                        >
                          Generate
                        </Button>
                      </div>
                    </div>
                  )
                : undefined
            }
            tableClassName="task-table"
          />
        </div>
      </div>

      {/* Modals */}
      {showBusinessModal && missingDetailsData && (
        <MissingBusinessDetailsModal
          isOpen={showBusinessModal}
          onClose={() => {
            setShowBusinessModal(false);
            setMissingDetailsData(null);
          }}
          onComplete={handleBusinessDetailsComplete}
          {...missingDetailsData}
        />
      )}

      {showInvoiceModal && selectedInvoice && (
        <InvoiceModal
          isOpen={showInvoiceModal}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedInvoice(null);
          }}
          invoice={selectedInvoice}
        />
      )}
    </div>
  );
};